import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import {
  PageContainer, StatCard, PremiumCard, DataTable, StatusBadge, Avatar, Button,
  LoadingState, EmptyState,
} from "components/ui";

const IconBuilding = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" />
    <path d="M12 6h.01" /><path d="M12 10h.01" /><path d="M12 14h.01" />
    <path d="M16 10h.01" /><path d="M16 14h.01" /><path d="M8 10h.01" /><path d="M8 14h.01" />
  </svg>
);
const IconChart = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);
const IconCard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);
const IconCoins = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6" /><path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
    <path d="M7 6h1v4" /><path d="m16.71 13.88.7.71-2.82 2.82" />
  </svg>
);
const IconAlarm = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" />
    <path d="M5 3 2 6" /><path d="m22 6-3-3" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/* ── Dependency-free inline charts ─────────────────────── */

function RevenueTrendChart({ data }) {
  if (!data.length) return <EmptyState title="No revenue data yet" />;

  const width = 560, height = 200, padding = 40; // extra left padding for y-axis labels
  const values = data.map((d) => Number(d.mrr) || 0);
  const max = Math.max(...values, 1);
  const stepX = (width - padding * 2) / Math.max(data.length - 1, 1);

  const points = values.map((v, i) => {
    const x = padding + i * stepX;
    const y = height - padding - (v / max) * (height - padding * 2);
    return [x, y];
  });

  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
  const areaPath = `${linePath} L${points[points.length - 1][0]},${height - padding} L${points[0][0]},${height - padding} Z`;

  // 5-tick y-axis (0%, 25%, 50%, 75%, 100% of max)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    y: height - padding - f * (height - padding * 2),
    value: f * max,
  }));

  const formatK = (v) => `$${(v / 1000).toFixed(1)}k`;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height + 24}`} style={{ display: "block" }}>
      <defs>
        <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* y-axis gridlines + labels */}
      {yTicks.map((t, i) => (
        <g key={i}>
          <line
            x1={padding} y1={t.y} x2={width - padding} y2={t.y}
            stroke="var(--border-subtle)" strokeWidth="1"
            strokeDasharray={i === 0 ? "0" : "2,3"}
          />
          <text x={padding - 8} y={t.y + 4} textAnchor="end" fontSize="10" fill="var(--text-muted)">
            {formatK(t.value)}
          </text>
        </g>
      ))}

      <path d={areaPath} fill="url(#mrrFill)" />
      <path d={linePath} fill="none" stroke="var(--brand-primary)" strokeWidth="2.5" />

      {points.map(([x, y], i) => (
        <g key={i}>
          {/* larger invisible hit-area makes hovering easier than just the 4px dot */}
          <circle cx={x} cy={y} r="10" fill="transparent" style={{ cursor: "pointer" }}>
            <title>{`${data[i].label}: ${formatK(values[i])}`}</title>
          </circle>
          <circle cx={x} cy={y} r="4" fill="var(--brand-primary)" style={{ pointerEvents: "none" }} />
          <text x={x} y={height - padding + 18} textAnchor="middle" fontSize="11" fill="var(--text-muted)">
            {data[i].label ? data[i].label.split(" ")[0] : "—"}
          </text>
        </g>
      ))}
    </svg>
  );
}

function PlanDistributionDonut({ data }) {
  if (!data.length) return <EmptyState title="No active subscriptions" />;

  const colors = ["#6366f1", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444"];
  const total = data.reduce((s, d) => s + Number(d.tenantCount), 0) || 1;
  const size = 160, radius = 60, stroke = 22;
  const cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  let offsetAcc = 0;
  const arcs = data.map((d, i) => {
    const fraction = Number(d.tenantCount) / total;
    const dash = fraction * circumference;
    const arc = {
      dash,
      gap: circumference - dash,
      offset: -offsetAcc * circumference,
      color: colors[i % colors.length],
      label: d.planName,
      count: d.tenantCount,
      pct: Math.round(fraction * 100),
    };
    offsetAcc += fraction;
    return arc;
  });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {arcs.map((a, i) => (
          <circle
            key={i}
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke={a.color}
            strokeWidth={stroke}
            strokeDasharray={`${a.dash} ${a.gap}`}
            strokeDashoffset={a.offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ cursor: "pointer" }}
          >
            <title>{`${a.label}: ${a.count} tenant${a.count === 1 ? "" : "s"} (${a.pct}%)`}</title>
          </circle>
        ))}
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.map((d, i) => (
          <div key={d.planName} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: colors[i % colors.length] }} />
            <span>{d.planName}</span>
            <span className="t-muted t-mono" style={{ marginLeft: "auto" }}>{d.tenantCount}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main dashboard ─────────────────────────────────────── */

const SystemAdminDashboard = () => {
  const axiosPrivate = useAxiosPrivate();

  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [planDistribution, setPlanDistribution] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, tenantsRes, subsRes, trendRes, distRes] = await Promise.all([
          axiosPrivate.get("/api/admin/dashboard/stats"),
          axiosPrivate.get("/api/admin/dashboard/tenants"),
          axiosPrivate.get("/api/admin/dashboard/subscriptions"),
          axiosPrivate.get("/api/admin/dashboard/revenue-trend?months=6"),
          axiosPrivate.get("/api/admin/dashboard/plan-distribution"),
        ]);
        setStats(statsRes.data);
        setTenants(tenantsRes.data);
        setSubscriptions(subsRes.data);
        setRevenueTrend(trendRes.data);
        setPlanDistribution(distRes.data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [axiosPrivate]);

  if (loading || !stats) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState label="Loading platform overview…" /></PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* KPI strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
          marginBottom: 32,
        }}
      >
        <StatCard label="Tenants" value={stats.tenantCount} icon={<IconBuilding />}
                  delta={stats.newTenantsThisMonth} deltaLabel="" hint="this month" />
        <StatCard label="Active plans" value={stats.activePlanCount} icon={<IconChart />} />
        <StatCard label="Active subscriptions" value={stats.subscriptionCount} icon={<IconCard />} />
        <StatCard label="MRR" value={`$${(Number(stats.mrr) / 1000).toFixed(1)}k`} icon={<IconCoins />} />
        <StatCard
          label="Expiring soon"
          value={stats.expiringSoonCount}
          icon={<IconAlarm />}
          hint="within 7d"
          accent={{ bg: "var(--danger-bg)", fg: "var(--danger)" }}
        />
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16, marginBottom: 16 }}>
        <PremiumCard title="MRR trend" subtitle="Last 6 months">
          <RevenueTrendChart data={revenueTrend} />
        </PremiumCard>
        <PremiumCard title="Plan distribution" subtitle="Active subscriptions by plan">
          <PlanDistributionDonut data={planDistribution} />
        </PremiumCard>
      </div>

      {/* Tables */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 16 }}>
        <DataTable
          title="Tenants"
          subtitle="Most recently onboarded"
          toolbar={
            <Link to="/admin/list-plan" style={{ textDecoration: "none" }}>
              <Button variant="secondary" size="sm" leftIcon={<IconPlus />}>New tenant</Button>
            </Link>
          }
          search={false}
          paginate={false}
          data={tenants}
          rowKey={(r) => r.id}
          emptyTitle="No tenants yet"
          columns={[
            {
              key: "name", header: "Tenant",
              render: (t) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar name={t.name} size="sm" />
                  <span style={{ fontWeight: 600 }}>{t.name}</span>
                </div>
              ),
            },
            { key: "planName", header: "Plan",   render: (t) => <StatusBadge value={t.planName} /> },
            { key: "status",   header: "Status", render: (t) => <StatusBadge value={t.status} dot /> },
          ]}
        />

        <DataTable
          title="Subscriptions"
          subtitle="Upcoming renewals"
          toolbar={
            <Link to="/admin/list-plan" style={{ textDecoration: "none" }}>
              <Button variant="ghost" size="sm">View all →</Button>
            </Link>
          }
          search={false}
          paginate={false}
          data={subscriptions}
          rowKey={(r) => r.id}
          emptyTitle="No active subscriptions"
          columns={[
            {
              key: "tenantName", header: "Tenant",
              render: (s) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar name={s.tenantName} size="sm" />
                  <span>{s.tenantName}</span>
                </div>
              ),
            },
            { key: "planName", header: "Plan", render: (s) => <StatusBadge value={s.planName} /> },
            {
              key: "renewalDate", header: "Renewal",
              render: (s) => {
                if (!s.renewalDate) return "—";
                const d = new Date(s.renewalDate);
                const days = Math.ceil((d - new Date()) / 86400000);
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span>{d.toLocaleDateString()}</span>
                    <span className={days <= 7 ? "t-danger" : "t-muted"} style={{ fontSize: 11 }}>
                      {days}d left
                    </span>
                  </div>
                );
              },
            },
            { key: "amount", header: "Amount", align: "right", render: (s) => <strong>${Number(s.amount).toFixed(2)}</strong> },
            { key: "status", header: "Status", render: (s) => <StatusBadge value={s.status} dot /> },
          ]}
        />
      </div>
    </PageContainer>
  );
};

export default SystemAdminDashboard;